// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md #6
// Pipeline kanban with @dnd-kit drag-drop between stage columns.
// POST /pipeline/cards/{property_id}/move on drop with optimistic update.
import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { hasTokens } from '../lib/auth'
import { usePipeline } from '../hooks/usePipeline'
import {
  movePipelineCard,
  type PipelineCard,
  type PipelineResponse,
  type PipelineStage,
} from '../lib/api'
import { useMe } from '../hooks/useAuth'
import { canWriteParticipation } from '../lib/permissions'
import { CardSkeleton } from '../components/Skeleton'

type PipelineSearch = { q: string }

export const Route = createFileRoute('/pipeline')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): PipelineSearch => ({
    q: typeof search.q === 'string' ? search.q : '',
  }),
  component: PipelinePage,
})

const STAGE_CSS_KEY: Record<string, string> = {
  bacheca: 'bacheca',
  scd_richiesta: 'scd-richiesta',
  scd_pronta_fissare: 'scd-pronta',
  scd_fissate: 'scd-fissate',
  attesa_esito: 'attesa-esito',
  esito_pos_checklist: 'esito-pos',
  domanda_asta: 'domanda-asta',
  data_vendita: 'data-vendita',
  esito_neg_scd: 'esito-neg',
  non_aggiudicata: 'non-aggiudicata',
  aggiudicata: 'aggiudicata',
}

function DraggableCard({
  card,
  disabled,
}: {
  card: PipelineCard
  disabled: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: card.id,
      disabled,
      data: { card },
    })

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.4 : 1,
    cursor: disabled ? 'default' : 'grab',
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="kanban-card"
      style={{
        ...style,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-2) var(--sp-3)',
        fontSize: 'var(--fs-12)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
      aria-label={`Trascina ${card.name}`}
    >
      <div
        style={{
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--ink-1)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={card.name}
      >
        {card.name}
      </div>
      {card.due && (
        <div className="muted mono" style={{ fontSize: 'var(--fs-10)' }}>
          ⏱{' '}
          {new Date(card.due).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
          })}
        </div>
      )}
      {card.labels.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {card.labels.slice(0, 3).map((l) => (
            <span
              key={l}
              className="chip outline"
              style={{ fontSize: 9, padding: '1px 5px' }}
            >
              {l}
            </span>
          ))}
        </div>
      )}
      {(card.checklist_total > 0 || card.has_attachments) && (
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            display: 'flex',
            gap: 8,
          }}
        >
          {card.checklist_total > 0 && (
            <span>
              ☑ {card.checklist_done}/{card.checklist_total}
            </span>
          )}
          {card.has_attachments && <span>📎</span>}
        </div>
      )}
    </div>
  )
}

function DroppableColumn({
  stage,
  disabled,
}: {
  stage: PipelineStage
  disabled: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.stage,
    data: { stage: stage.stage },
  })
  const cls = STAGE_CSS_KEY[stage.stage] ?? ''

  return (
    <div
      ref={setNodeRef}
      className={`kanban-col ${cls}${isOver ? ' drop-over' : ''}`}
      style={{
        background: 'var(--bg-2)',
        border: `1px solid ${isOver ? 'var(--accent-ink)' : 'var(--line)'}`,
        borderRadius: 'var(--r-md)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 240,
        maxHeight: '70vh',
        transition: 'border-color var(--dur-fast)',
      }}
      aria-label={`Colonna ${stage.label}`}
    >
      <div
        className="kanban-col-head"
        style={{
          padding: 'var(--sp-2) var(--sp-3)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          background: 'var(--surface)',
          borderRadius: 'var(--r-md) var(--r-md) 0 0',
        }}
      >
        <span
          className="ttl"
          style={{
            fontSize: 'var(--fs-12)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--ink-1)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {stage.label}
        </span>
        <span
          className="ct"
          style={{
            background: 'var(--bg-2)',
            color: 'var(--ink-3)',
            padding: '1px 6px',
            borderRadius: 'var(--r-sm)',
            fontSize: 'var(--fs-11)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {stage.count}
        </span>
      </div>
      <div
        style={{
          padding: 'var(--sp-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          overflow: 'auto',
          flex: 1,
        }}
      >
        {stage.cards.map((c) => (
          <DraggableCard key={c.id} card={c} disabled={disabled} />
        ))}
        {stage.cards.length === 0 && (
          <div
            className="muted"
            style={{
              padding: 'var(--sp-3)',
              fontSize: 'var(--fs-11)',
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Vuoto
          </div>
        )}
      </div>
    </div>
  )
}

function PipelinePage() {
  const { data: me } = useMe()
  const { data, isLoading } = usePipeline({})
  const canEdit = canWriteParticipation(me)
  const qc = useQueryClient()
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  )
  const [activeCard, setActiveCard] = useState<PipelineCard | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const moveMut = useMutation({
    mutationFn: ({
      propertyId,
      from_stage,
      to_stage,
    }: {
      propertyId: string
      from_stage: string
      to_stage: string
    }) => movePipelineCard(propertyId, { from_stage, to_stage }),
    onMutate: async ({ propertyId, from_stage, to_stage }) => {
      await qc.cancelQueries({ queryKey: ['pipeline'] })
      const prev = qc.getQueryData<PipelineResponse>(['pipeline', {}])
      if (prev) {
        const next: PipelineResponse = {
          ...prev,
          stages: prev.stages.map((s) => {
            if (s.stage === from_stage) {
              const card = s.cards.find((c) => c.id === propertyId)
              if (!card) return s
              return {
                ...s,
                cards: s.cards.filter((c) => c.id !== propertyId),
                count: Math.max(0, s.count - 1),
              }
            }
            if (s.stage === to_stage) {
              const fromCol = prev.stages.find((x) => x.stage === from_stage)
              const card = fromCol?.cards.find((c) => c.id === propertyId)
              if (!card) return s
              return {
                ...s,
                cards: [card, ...s.cards],
                count: s.count + 1,
              }
            }
            return s
          }),
        }
        qc.setQueryData(['pipeline', {}], next)
      }
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['pipeline', {}], ctx.prev)
      setMoveError(err instanceof Error ? err.message : 'Errore spostamento')
      setTimeout(() => setMoveError(null), 4000)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['pipeline'] })
    },
  })

  function onDragStart(e: DragStartEvent) {
    setActiveCard((e.active.data.current?.card as PipelineCard) ?? null)
    setMoveError(null)
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveCard(null)
    if (!e.over) return
    const card = e.active.data.current?.card as PipelineCard | undefined
    const to_stage = e.over.id as string
    if (!card) return
    // Find source stage from current cache.
    const stages = data?.stages ?? []
    const src = stages.find((s) => s.cards.some((c) => c.id === card.id))
    if (!src) return
    if (src.stage === to_stage) return
    moveMut.mutate({
      propertyId: card.id,
      from_stage: src.stage,
      to_stage,
    })
  }

  const total = data?.total ?? 0
  const stages = data?.stages ?? []
  const active = stages
    .filter(
      (s) =>
        !['aggiudicata', 'non_aggiudicata', 'esito_neg_scd', 'scartata'].includes(
          s.stage,
        ),
    )
    .reduce((sum, s) => sum + s.count, 0)

  return (
    <>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--accent-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-2) var(--sp-3)',
          margin: '0 0 var(--sp-3) 0',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
          fontSize: 'var(--fs-12)',
        }}
      >
        <span>
          <b>{total}</b> carte totali · <b>{active}</b> attive
        </span>
        {!canEdit && (
          <span className="chip outline">Sola lettura</span>
        )}
        {moveMut.isPending && (
          <span className="chip info">Sposto…</span>
        )}
        {moveError && (
          <span className="chip risk" role="alert">
            {moveError}
          </span>
        )}
      </div>

      {isLoading && <CardSkeleton />}

      {data && (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div
            className="kanban-board"
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'minmax(240px, 1fr)',
              gap: 'var(--sp-3)',
              overflowX: 'auto',
              paddingBottom: 'var(--sp-2)',
            }}
          >
            {stages.map((s) => (
              <DroppableColumn
                key={s.stage}
                stage={s}
                disabled={!canEdit || moveMut.isPending}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? (
              <DraggableCard card={activeCard} disabled={false} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </>
  )
}
